import { auditReports } from '@mi-front/api-clients';
import { ChipNavigationMui, ChipNavigationProps } from '@mi-front/ui';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningIcon from '@mui/icons-material/Warning';
import { useIntl } from 'react-intl';
import { GipReportFutureSchedulesView } from '../gip-report/preview/components/result/GipReportFutureSchedulesView';
import { GipReportMatchedSchedulesView } from '../gip-report/preview/components/result/GipReportMatchedSchedulesView';
import { GipReportMissingMaterialsView } from '../gip-report/preview/components/result/GipReportMissingMaterialsView';
import { GipReportUnmatchedSchedulesView } from '../gip-report/preview/components/result/GipReportUnmatchedSchedulesView';
import { GipReportUnprocessedSchedulesView } from '../gip-report/preview/components/result/GipReportUnprocessedSchedulesView';

export interface GipReportChipNavigationProps {
  // Result object with schedule data
  result: auditReports.GipReportResultDto;

  // Optional custom view components (defaults to GIP report views)
  MatchedSchedulesView?: React.ComponentType<{
    schedules: auditReports.GipReportMatchedScheduleDto[];
  }>;
  UnmatchedSchedulesView?: React.ComponentType<{
    schedules: auditReports.GipReportScheduleDto[];
  }>;
  UnprocessedSchedulesView?: React.ComponentType<{
    schedules: auditReports.GipReportScheduleDto[];
  }>;
  FutureSchedulesView?: React.ComponentType<{
    schedules: auditReports.GipReportScheduleDto[];
  }>;
  MissingMaterialsView?: React.ComponentType<{
    audiosStatusByStation: {
      [key: string]: auditReports.AudioStatusDto[];
    };
  }>;

  // Optional additional chips to add at the beginning
  additionalChips?: ChipNavigationProps['chips'];

  // Optional action component to display at the top right of the navigation
  actionComponent?: React.ReactNode;

  // Configuration
  defaultActiveChip?: string;
}

export const GipReportChipNavigation = ({
  result,
  MatchedSchedulesView = GipReportMatchedSchedulesView,
  UnmatchedSchedulesView = GipReportUnmatchedSchedulesView,
  UnprocessedSchedulesView = GipReportUnprocessedSchedulesView,
  FutureSchedulesView = GipReportFutureSchedulesView,
  MissingMaterialsView = GipReportMissingMaterialsView,
  additionalChips = [],
  actionComponent,
  defaultActiveChip = 'with-matches',
}: GipReportChipNavigationProps) => {
  const { $t } = useIntl();

  const {
    matchedSchedules,
    unmatchedSchedules,
    unprocessedSchedules,
    futureSchedules,
    audiosStatusByStation,
  } = result;

  // Calculate missing materials count
  const missingMaterialsCount = Object.values(audiosStatusByStation || {}).reduce(
    (total, audioStatuses) =>
      total + audioStatuses.filter((audio) => audio.audioId === null).length,
    0,
  );

  const standardChips: ChipNavigationProps['chips'] = [
    {
      value: 'with-matches',
      label: `${$t({
        id: 'audit.gipReport.result.tabs.withMatches',
        defaultMessage: 'Con matches',
      })} (${matchedSchedules.length})`,
      icon: <CheckIcon />,
      content: <MatchedSchedulesView schedules={matchedSchedules} />,
    },
    {
      value: 'without-matches',
      label: `${$t({
        id: 'audit.gipReport.result.tabs.withoutMatches',
        defaultMessage: 'Sin matches',
      })} (${unmatchedSchedules.length})`,
      icon: <CloseIcon />,
      content: <UnmatchedSchedulesView schedules={unmatchedSchedules} />,
    },
    {
      value: 'to-be-processed',
      label: `${$t({
        id: 'audit.gipReport.result.tabs.toBeProcessed',
        defaultMessage: 'Por procesar',
      })} (${unprocessedSchedules.length})`,
      icon: <HourglassEmptyIcon />,
      content: <UnprocessedSchedulesView schedules={unprocessedSchedules} />,
    },
    {
      value: 'future',
      label: `${$t({
        id: 'audit.gipReport.result.tabs.future',
        defaultMessage: 'Futuros',
      })} (${futureSchedules.length})`,
      icon: <DateRangeIcon />,
      content: <FutureSchedulesView schedules={futureSchedules} />,
    },
    // Only show missing materials tab if there are missing materials
    ...(missingMaterialsCount > 0
      ? [
          {
            value: 'missing-materials',
            label: `${$t({
              id: 'audit.gipReport.result.tabs.missingMaterials',
              defaultMessage: 'Materiales faltantes',
            })} (${missingMaterialsCount})`,
            icon: <WarningIcon />,
            color: 'warning' as const,
            content: <MissingMaterialsView audiosStatusByStation={audiosStatusByStation} />,
          },
        ]
      : []),
  ];

  const chips = [...additionalChips, ...standardChips];

  return (
    <ChipNavigationMui
      chips={chips}
      defaultActiveChip={defaultActiveChip}
      header={actionComponent}
    />
  );
};
